import * as THREE from 'three'
import { Controls } from 'three'
import { CONTROL_DAMPING_FACTOR, FIRST_PERSON_MOVEMENT_SPEED } from '@/shared/constants'
import { CameraMode } from '@/shared/types/core'
import {
  accumulateWheelInputDelta,
  computeRollFreeUp,
  DEFAULT_WHEEL_ABSOLUTE_MIN_STEP,
  DEFAULT_WHEEL_MIN_STEP_RATIO,
  isCurrentViewportCamera,
  NATIVE_CONTROL_SURFACE_KINDS,
  type NativeControlServices,
  resolveNavigationScale,
  resolveWheelForwardStep,
  resolveWheelInputDelta,
  shouldUseAsyncPicking,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

export interface CameraFreeLookControllerSettings {
  /** Pointer pan scale relative to movement speed per CSS pixel. */
  panPixelRatio?: number
}

/** CameraFreeLookController constructor options. */
export interface CameraFreeLookControllerOptions extends CameraFreeLookControllerSettings {
  /** Shared services provided by ControlPlugin. */
  services: NativeControlServices
}

export interface CameraFreeLookCollisionQuery {
  position: THREE.Vector3
  target: THREE.Vector3
  up: THREE.Vector3
  translation: THREE.Vector3
}

export interface CameraFreeLookCollisionResult {
  translation: THREE.Vector3
}

export type CameraFreeLookCollisionResolver = (
  query: CameraFreeLookCollisionQuery
) => CameraFreeLookCollisionResult | THREE.Vector3 | null | undefined

interface TwoPointerGesture {
  centroid: THREE.Vector2
  distance: number
}

const DEFAULT_LOOK_SENSITIVITY = 0.003
const DEFAULT_PAN_PIXEL_RATIO = 0.002
const DEFAULT_PINCH_PIXEL_RATIO = 0.01
const DEFAULT_WHEEL_STEP_RATIO = 0.2
const MIN_PITCH = THREE.MathUtils.degToRad(-85)
const MAX_PITCH = THREE.MathUtils.degToRad(85)
const MIN_TARGET_DISTANCE = 0.1
const DOUBLE_CLICK_FOCUS_RATIO = 0.2
const DOUBLE_CLICK_MIN_DISTANCE = 0.2
const DOUBLE_CLICK_DURATION_MS = 300
const ROTATION_MOMENTUM_MIN_SPEED = 0.002
const ROTATION_MOMENTUM_MAX_SAMPLE_AGE_MS = 80
const ROTATION_MOMENTUM_HALF_LIFE_SECONDS = Math.max(CONTROL_DAMPING_FACTOR, 0.05)
const MOVEMENT_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE'])

/** Camera-only free-look controller for touch, mouse, and WASDQE navigation. */
export class CameraFreeLookController extends Controls<THREE.Camera> {
  private readonly services: NativeControlServices
  private readonly activePointers = new Map<number, THREE.Vector2>()
  private readonly pointerButtons = new Map<number, number>()
  private readonly keyState = new Set<string>()
  private readonly externalMoveInput = { forward: 0, right: 0, up: 0 }
  private readonly externalLookInput = { yaw: 0, pitch: 0 }
  private collisionResolver: CameraFreeLookCollisionResolver | null = null

  private lastSinglePointer: THREE.Vector2 | null = null
  private lastTwoPointerGesture: TwoPointerGesture | null = null
  private yaw = 0
  private pitch = 0
  private targetDistance = 1
  private connected = false
  private externalMoveInputActive = false
  private externalLookInputActive = false
  private rotationMomentumYawVelocity = 0
  private rotationMomentumPitchVelocity = 0
  private lastRotationSampleAt = 0
  private wheelDelta = 0
  private pickRequestVersion = 0
  private pickAbortController: AbortController | null = null

  private readonly handlePointerDownBound: EventListener = (event) =>
    this.handlePointerDown(event as PointerEvent)
  private readonly handlePointerMoveBound: EventListener = (event) =>
    this.handlePointerMove(event as PointerEvent)
  private readonly handlePointerUpBound: EventListener = (event) =>
    this.handlePointerUp(event as PointerEvent)
  private readonly handleWheelBound: EventListener = (event) =>
    this.handleWheel(event as WheelEvent)
  private readonly handleDoubleClickBound: EventListener = (event) =>
    this.handleDoubleClick(event as MouseEvent)
  private readonly handleContextMenuBound: EventListener = (event) =>
    this.handleContextMenu(event as MouseEvent)
  private readonly handleKeyDownBound = (event: KeyboardEvent) => this.handleKeyDown(event)
  private readonly handleKeyUpBound = (event: KeyboardEvent) => this.handleKeyUp(event)

  /** Mouse/touch look sensitivity in radians per CSS pixel. */
  lookSensitivity = DEFAULT_LOOK_SENSITIVITY
  /** External look speed in radians per second. */
  externalLookSpeed = 1.2
  /** Pointer pan scale relative to movement speed per CSS pixel. */
  panPixelRatio = DEFAULT_PAN_PIXEL_RATIO
  /** Pinch movement scale relative to movement speed per CSS pixel. */
  pinchPixelRatio = DEFAULT_PINCH_PIXEL_RATIO
  /** Keyboard movement speed in world units per second. */
  movementSpeed = FIRST_PERSON_MOVEMENT_SPEED

  constructor(options: CameraFreeLookControllerOptions) {
    super(new THREE.PerspectiveCamera(), null)
    this.services = options.services
    this.panPixelRatio = resolvePanPixelRatio(options.panPixelRatio, DEFAULT_PAN_PIXEL_RATIO)
    this.enabled = false
  }

  override connect(element: HTMLElement): void {
    if (this.connected) {
      return
    }

    if (this.services.rig.mode !== CameraMode.Perspective) {
      this.services.rig.setState({
        ...this.services.rig.getState(),
        mode: CameraMode.Perspective,
      })
    }

    super.connect(element)
    this.connected = true
    this.syncAnglesFromRig()
    element.addEventListener('pointerdown', this.handlePointerDownBound, { passive: false })
    element.addEventListener('pointermove', this.handlePointerMoveBound, { passive: false })
    element.addEventListener('pointerup', this.handlePointerUpBound, { passive: false })
    element.addEventListener('pointercancel', this.handlePointerUpBound, { passive: false })
    element.addEventListener('wheel', this.handleWheelBound, { passive: false })
    element.addEventListener('dblclick', this.handleDoubleClickBound)
    element.addEventListener('contextmenu', this.handleContextMenuBound)
    window.addEventListener('keydown', this.handleKeyDownBound, true)
    window.addEventListener('keyup', this.handleKeyUpBound, true)
  }

  override disconnect(): void {
    if (!this.connected) {
      return
    }

    const element = this.domElement
    element?.removeEventListener('pointerdown', this.handlePointerDownBound)
    element?.removeEventListener('pointermove', this.handlePointerMoveBound)
    element?.removeEventListener('pointerup', this.handlePointerUpBound)
    element?.removeEventListener('pointercancel', this.handlePointerUpBound)
    element?.removeEventListener('wheel', this.handleWheelBound)
    element?.removeEventListener('dblclick', this.handleDoubleClickBound)
    element?.removeEventListener('contextmenu', this.handleContextMenuBound)
    window.removeEventListener('keydown', this.handleKeyDownBound, true)
    window.removeEventListener('keyup', this.handleKeyUpBound, true)
    this.connected = false
    this.cancelPendingPickRequest()
    this.resetTransientInput()
    super.disconnect()
  }

  override dispose(): void {
    this.disconnect()
    this.setCollisionResolver(null)
  }

  setExternalMoveInput(input: { forward?: number; right?: number; up?: number } | null): void {
    const forward = clampAxis(input?.forward ?? 0)
    const right = clampAxis(input?.right ?? 0)
    const up = clampAxis(input?.up ?? 0)
    const nextActive = forward !== 0 || right !== 0 || up !== 0
    if (nextActive && !this.externalMoveInputActive) {
      this.syncAnglesFromRig()
    }
    this.externalMoveInput.forward = forward
    this.externalMoveInput.right = right
    this.externalMoveInput.up = up
    this.externalMoveInputActive = nextActive
  }

  setExternalLookInput(input: { yaw?: number; pitch?: number } | null): void {
    const yaw = clampAxis(input?.yaw ?? 0)
    const pitch = clampAxis(input?.pitch ?? 0)
    const nextActive = yaw !== 0 || pitch !== 0
    if (nextActive && !this.externalLookInputActive) {
      this.syncAnglesFromRig()
    }
    if (nextActive) {
      this.stopRotationMomentum()
    }
    this.externalLookInput.yaw = yaw
    this.externalLookInput.pitch = pitch
    this.externalLookInputActive = nextActive
  }

  setCollisionResolver(resolver: CameraFreeLookCollisionResolver | null): void {
    this.collisionResolver = resolver
  }

  syncFromCamera(): void {
    this.stopRotationMomentum()
    this.syncAnglesFromRig()
  }

  override update(delta: number): void {
    if (!this.enabled || delta <= 0) {
      return
    }

    this.updateRotationMomentum(delta)
    this.applyExternalLookInput(delta)
    this.applyWheelInput()
    const move = this.resolveKeyboardMoveVector()
    if (move.lengthSq() > 0) {
      move.clampLength(0, 1).multiplyScalar(this.movementSpeed * delta)
      this.translateCamera(move)
    }
  }

  private handlePointerDown(event: PointerEvent): void {
    if (!this.enabled || (event.button !== 0 && event.button !== 2)) {
      return
    }

    this.stopRotationMomentum()
    if (this.activePointers.size === 0) {
      this.syncAnglesFromRig()
    }
    this.domElement?.focus()
    this.activePointers.set(event.pointerId, new THREE.Vector2(event.clientX, event.clientY))
    this.pointerButtons.set(event.pointerId, event.button)
    this.resetGestureSnapshots()
    this.domElement?.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.enabled || !this.activePointers.has(event.pointerId)) {
      return
    }

    this.activePointers.set(event.pointerId, new THREE.Vector2(event.clientX, event.clientY))
    if (this.activePointers.size === 1) {
      this.applySinglePointerGesture(event.pointerId)
    } else {
      this.applyTwoPointerGesture()
    }
    event.preventDefault()
  }

  private handlePointerUp(event: PointerEvent): void {
    const button = this.pointerButtons.get(event.pointerId)
    if (!this.activePointers.delete(event.pointerId)) {
      return
    }

    this.pointerButtons.delete(event.pointerId)
    this.domElement?.releasePointerCapture?.(event.pointerId)
    this.resetGestureSnapshots()
    if (button === 0 && this.activePointers.size === 0) {
      this.startRotationMomentum()
    }
    event.preventDefault()
  }

  private handleWheel(event: WheelEvent): void {
    if (!this.enabled) {
      return
    }

    const wheelStep = resolveWheelInputDelta(event.deltaY, event.deltaMode)
    if (wheelStep === 0) {
      return
    }

    this.stopRotationMomentum()
    this.syncAnglesFromRig()
    this.wheelDelta = accumulateWheelInputDelta(this.wheelDelta, wheelStep)
    event.preventDefault()
  }

  private applyWheelInput(): void {
    if (this.wheelDelta === 0) {
      return
    }

    const translationScale = this.resolveTranslationScale()
    const navigationScale = resolveNavigationScale({
      cameraTargetDistance: translationScale,
      sceneBounds: null,
    })
    const distance = resolveWheelForwardStep({
      cameraTargetDistance: translationScale,
      wheelDelta: this.wheelDelta,
      proportionalStepRatio: DEFAULT_WHEEL_STEP_RATIO,
      navigationScale,
      minStepRatio: DEFAULT_WHEEL_MIN_STEP_RATIO,
      absoluteMinStep: DEFAULT_WHEEL_ABSOLUTE_MIN_STEP,
    })
    this.wheelDelta = 0
    this.moveCameraForward(distance)
  }

  private handleDoubleClick(event: MouseEvent): void {
    if (!this.enabled || event.defaultPrevented || !this.services.surfaceQuery) {
      return
    }

    const camera = syncViewportCameraFromRig(this.services)
    if (shouldUseAsyncPicking(this.services)) {
      event.preventDefault()
      this.scheduleDoubleClickPick(event, camera)
      return
    }

    const hit = this.services.surfaceQuery.queryAtScreen(
      new THREE.Vector2(event.clientX, event.clientY),
      camera,
      {
        kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
        interactionType: 'click',
      }
    )
    if (!hit) {
      return
    }

    this.stopRotationMomentum()
    void this.focusOnPoint(hit.point)
    event.preventDefault()
  }

  private scheduleDoubleClickPick(event: MouseEvent, camera: THREE.Camera): void {
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    void this.services.surfaceQuery!.queryAtScreenAsync!(
      new THREE.Vector2(event.clientX, event.clientY),
      camera,
      {
        kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
        interactionType: 'click',
        signal: controller.signal,
      }
    )
      .then((hit) => {
        if (
          hit &&
          version === this.pickRequestVersion &&
          this.enabled &&
          isCurrentViewportCamera(this.services, camera)
        ) {
          this.stopRotationMomentum()
          void this.focusOnPoint(hit.point)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private cancelPendingPickRequest(): void {
    this.pickRequestVersion += 1
    this.pickAbortController?.abort()
    this.pickAbortController = null
  }

  private handleContextMenu(event: MouseEvent): void {
    if (!this.enabled) {
      return
    }

    event.preventDefault()
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.enabled || !MOVEMENT_KEYS.has(event.code)) {
      return
    }

    if (this.keyState.size === 0) {
      this.syncAnglesFromRig()
    }
    this.keyState.add(event.code)
    event.preventDefault()
  }

  private handleKeyUp(event: KeyboardEvent): void {
    if (!this.enabled || !MOVEMENT_KEYS.has(event.code)) {
      return
    }

    this.keyState.delete(event.code)
    event.preventDefault()
  }

  private applySinglePointerGesture(pointerId: number): void {
    const pointer = this.activePointers.values().next().value as THREE.Vector2 | undefined
    if (!pointer) {
      return
    }

    if (this.lastSinglePointer) {
      const delta = pointer.clone().sub(this.lastSinglePointer)
      if (this.pointerButtons.get(pointerId) === 2) {
        this.panCamera(delta)
      } else {
        this.rotateCamera(delta)
      }
    }
    this.lastSinglePointer = pointer.clone()
    this.lastTwoPointerGesture = null
  }

  private applyTwoPointerGesture(): void {
    const gesture = this.resolveTwoPointerGesture()
    if (!gesture) {
      return
    }

    if (this.lastTwoPointerGesture) {
      const deltaCentroid = gesture.centroid.clone().sub(this.lastTwoPointerGesture.centroid)
      const deltaDistance = gesture.distance - this.lastTwoPointerGesture.distance
      this.panCamera(deltaCentroid)
      this.moveCameraForward(deltaDistance * this.resolveTranslationScale() * this.pinchPixelRatio)
    }

    this.lastTwoPointerGesture = gesture
    this.lastSinglePointer = null
  }

  private rotateCamera(delta: THREE.Vector2): void {
    const now = performance.now()
    const yawDelta = -delta.x * this.lookSensitivity
    const pitchDelta = -delta.y * this.lookSensitivity
    const elapsedSeconds = (now - this.lastRotationSampleAt) / 1000
    if (
      this.lastRotationSampleAt > 0 &&
      elapsedSeconds > 0 &&
      elapsedSeconds < ROTATION_MOMENTUM_MAX_SAMPLE_AGE_MS / 1000
    ) {
      this.rotationMomentumYawVelocity = yawDelta / elapsedSeconds
      this.rotationMomentumPitchVelocity = pitchDelta / elapsedSeconds
    }
    this.lastRotationSampleAt = now
    this.applyRotationDelta(yawDelta, pitchDelta)
  }

  private applyRotationDelta(yawDelta: number, pitchDelta: number): void {
    this.yaw += yawDelta
    this.pitch = THREE.MathUtils.clamp(this.pitch + pitchDelta, MIN_PITCH, MAX_PITCH)
    this.applyPoseFromAngles()
  }

  private applyExternalLookInput(delta: number): void {
    const yawAxis = this.externalLookInput.yaw
    const pitchAxis = this.externalLookInput.pitch
    if (yawAxis === 0 && pitchAxis === 0) {
      return
    }
    this.yaw -= yawAxis * this.externalLookSpeed * delta
    this.pitch = THREE.MathUtils.clamp(
      this.pitch - pitchAxis * this.externalLookSpeed * delta,
      MIN_PITCH,
      MAX_PITCH
    )
    this.applyPoseFromAngles()
  }

  private panCamera(delta: THREE.Vector2): void {
    const axes = this.resolveCameraAxes()
    const scale = this.resolveTranslationScale() * this.panPixelRatio
    const translation = axes.right
      .multiplyScalar(-delta.x * scale)
      .add(axes.up.multiplyScalar(delta.y * scale))
    this.translateCamera(translation)
  }

  private moveCameraForward(distance: number): void {
    if (distance === 0) {
      return
    }

    this.translateCamera(this.resolveForward().multiplyScalar(distance))
  }

  private async focusOnPoint(point: THREE.Vector3): Promise<void> {
    const previous = this.services.rig.getState()
    const forward = previous.target.clone().sub(previous.position)
    if (forward.lengthSq() <= 1e-12) {
      return
    }

    forward.normalize()
    const distanceToPoint = Math.max(previous.position.distanceTo(point), 1e-6)
    const focusDistance = THREE.MathUtils.clamp(
      distanceToPoint * DOUBLE_CLICK_FOCUS_RATIO,
      DOUBLE_CLICK_MIN_DISTANCE,
      distanceToPoint
    )
    const nextPosition = point.clone().addScaledVector(forward, -focusDistance)
    const translation = nextPosition.clone().sub(previous.position)
    const resolvedTranslation = this.resolveCollisionTranslation(previous, translation)
    const resolvedPosition = previous.position.clone().add(resolvedTranslation)

    await this.services.rig.animateTo(
      {
        position: resolvedPosition,
        target: point.clone(),
        up: previous.up.clone(),
        mode: CameraMode.Perspective,
      },
      DOUBLE_CLICK_DURATION_MS,
      { easing: 'cubicInOut' }
    )

    this.syncAnglesFromRig()
  }

  private updateRotationMomentum(delta: number): void {
    if (this.activePointers.size > 0 || !this.hasRotationMomentum()) {
      return
    }

    const yawDelta = this.rotationMomentumYawVelocity * delta
    const pitchDelta = this.rotationMomentumPitchVelocity * delta
    this.applyRotationDelta(yawDelta, pitchDelta)

    const decay = 0.5 ** (delta / ROTATION_MOMENTUM_HALF_LIFE_SECONDS)
    this.rotationMomentumYawVelocity *= decay
    this.rotationMomentumPitchVelocity *= decay
    if (!this.hasRotationMomentum()) {
      this.stopRotationMomentum()
    }
  }

  private startRotationMomentum(): void {
    if (
      performance.now() - this.lastRotationSampleAt > ROTATION_MOMENTUM_MAX_SAMPLE_AGE_MS ||
      !this.hasRotationMomentum()
    ) {
      this.stopRotationMomentum()
    }
  }

  private hasRotationMomentum(): boolean {
    return (
      Math.abs(this.rotationMomentumYawVelocity) > ROTATION_MOMENTUM_MIN_SPEED ||
      Math.abs(this.rotationMomentumPitchVelocity) > ROTATION_MOMENTUM_MIN_SPEED
    )
  }

  private stopRotationMomentum(): void {
    this.rotationMomentumYawVelocity = 0
    this.rotationMomentumPitchVelocity = 0
    this.lastRotationSampleAt = 0
  }

  private translateCamera(translation: THREE.Vector3): void {
    const state = this.services.rig.getState()
    const resolvedTranslation = this.resolveCollisionTranslation(state, translation)
    this.services.rig.setState({
      ...state,
      position: state.position.clone().add(resolvedTranslation),
      target: state.target.clone().add(resolvedTranslation),
      mode: CameraMode.Perspective,
    })
  }

  private resolveCollisionTranslation(
    state: ReturnType<NativeControlServices['rig']['getState']>,
    translation: THREE.Vector3
  ): THREE.Vector3 {
    if (!this.collisionResolver || translation.lengthSq() <= 0) {
      return translation
    }
    const result = this.collisionResolver({
      position: state.position.clone(),
      target: state.target.clone(),
      up: state.up.clone(),
      translation: translation.clone(),
    })
    if (result instanceof THREE.Vector3) {
      return result.clone()
    }
    if (result?.translation instanceof THREE.Vector3) {
      return result.translation.clone()
    }
    return translation
  }

  private applyPoseFromAngles(): void {
    const state = this.services.rig.getState()
    const forward = this.resolveForward()
    const distance = Math.max(this.targetDistance, MIN_TARGET_DISTANCE)
    this.services.rig.setState({
      ...state,
      target: state.position.clone().addScaledVector(forward, distance),
      up: computeRollFreeUp(forward),
      mode: CameraMode.Perspective,
    })
  }

  private resolveKeyboardMoveVector(): THREE.Vector3 {
    const axes = this.resolveCameraAxes()
    const forwardAxis = clampAxis(this.resolveAxis('KeyW', 'KeyS') + this.externalMoveInput.forward)
    const rightAxis = clampAxis(this.resolveAxis('KeyD', 'KeyA') + this.externalMoveInput.right)
    const upAxis = clampAxis(this.resolveAxis('KeyE', 'KeyQ') + this.externalMoveInput.up)
    return axes.forward
      .multiplyScalar(forwardAxis)
      .add(axes.right.multiplyScalar(rightAxis))
      .add(axes.up.multiplyScalar(upAxis))
  }

  private resolveAxis(positive: string, negative: string): number {
    const hasPositive = this.keyState.has(positive)
    const hasNegative = this.keyState.has(negative)
    if (hasPositive === hasNegative) {
      return 0
    }
    return hasPositive ? 1 : -1
  }

  private resolveCameraAxes(): {
    forward: THREE.Vector3
    right: THREE.Vector3
    up: THREE.Vector3
  } {
    const forward = this.resolveForward()
    const up = computeRollFreeUp(forward)
    const right = new THREE.Vector3().crossVectors(forward, up).normalize()
    return { forward, right, up }
  }

  private resolveForward(): THREE.Vector3 {
    const planar = Math.cos(this.pitch)
    return new THREE.Vector3(
      Math.cos(this.yaw) * planar,
      Math.sin(this.yaw) * planar,
      Math.sin(this.pitch)
    ).normalize()
  }

  private resolveTwoPointerGesture(): TwoPointerGesture | null {
    const [first, second] = Array.from(this.activePointers.values())
    if (!first || !second) {
      return null
    }

    return {
      centroid: first.clone().add(second).multiplyScalar(0.5),
      distance: first.distanceTo(second),
    }
  }

  private syncAnglesFromRig(): void {
    const state = this.services.rig.getState()
    const forward = state.target.clone().sub(state.position)
    this.targetDistance = Math.max(forward.length(), MIN_TARGET_DISTANCE)
    if (forward.lengthSq() <= 1e-12) {
      this.yaw = 0
      this.pitch = 0
      return
    }

    forward.normalize()
    this.yaw = Math.atan2(forward.y, forward.x)
    this.pitch = Math.asin(THREE.MathUtils.clamp(forward.z, -1, 1))
  }

  /** 平移灵敏度只依赖显式移动速度，避免等价视图因 target 向量长度不同而产生突变。 */
  private resolveTranslationScale(): number {
    const movementScale = Number.isFinite(this.movementSpeed)
      ? this.movementSpeed
      : FIRST_PERSON_MOVEMENT_SPEED
    return Math.max(movementScale, MIN_TARGET_DISTANCE)
  }

  private resetGestureSnapshots(): void {
    if (this.activePointers.size === 1) {
      const pointer = this.activePointers.values().next().value as THREE.Vector2 | undefined
      this.lastSinglePointer = pointer?.clone() ?? null
      this.lastTwoPointerGesture = null
      return
    }

    this.lastSinglePointer = null
    this.lastTwoPointerGesture = this.resolveTwoPointerGesture()
  }

  private resetTransientInput(): void {
    this.activePointers.clear()
    this.pointerButtons.clear()
    this.keyState.clear()
    this.setExternalMoveInput(null)
    this.setExternalLookInput(null)
    this.wheelDelta = 0
    this.stopRotationMomentum()
    this.lastSinglePointer = null
    this.lastTwoPointerGesture = null
  }
}

function clampAxis(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return THREE.MathUtils.clamp(value, -1, 1)
}

function resolvePanPixelRatio(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value) || value < 0) {
    return fallback
  }
  return value
}

export default CameraFreeLookController
